import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import UserTag from '../UserTag';
import VideoRenderer from './components/VideoRenderer';
import RippleEffect from './components/RippleEffect';
import { useVideoTransition } from './hooks/useVideoTransition';
import { useRippleAnimation } from './hooks/useRippleAnimation';
import style from './index.module.less';

interface IAiAvatarCardProps {
  showUserTag: boolean;
  className?: string;
  entryStandbyMode?: boolean;
}

/**
 * 数字人卡片组件
 * 使用组合模式，将复杂逻辑拆分到子组件和自定义hooks中
 */
function AiAvatarCard(props: IAiAvatarCardProps) {
  const { showUserTag, className, entryStandbyMode } = props;
  const room = useSelector((state: RootState) => state.room);
  const { scene } = room;
  const isFullScreen = false; // 全屏功能已简化

  // 使用自定义hooks管理状态和逻辑
  const { targetVideoSrc, canStart } = useVideoTransition({
    entryStandbyMode,
  });

  const { shouldShowRipples, rippleConfig } = useRippleAnimation({
    entryStandbyMode,
  });

  // 检查是否为缩放主题（layout2 / layout3 / layout4）
  // 移除不必要的 useMemo：DOM class 检查是 O(1) 操作，useMemo 的开销反而更大
  const docEl = document.documentElement;
  const bodyEl = document.body;
  const hasLayout2 = docEl.classList.contains('layout2-theme') || bodyEl.classList.contains('layout2-theme');
  const hasLayout3 = docEl.classList.contains('layout3-theme') || bodyEl.classList.contains('layout3-theme');
  const hasLayout4 = docEl.classList.contains('layout4-theme') || bodyEl.classList.contains('layout4-theme');

  // 根据主题计算 Canvas 尺寸（简化后的计算很快，不需要 memoization）
  let canvasSize: { width: number; height: number };
  if (hasLayout2) {
    // layout2 主题下使用更高分辨率，避免缩放后模糊
    canvasSize = { width: 1000, height: 1000 };
  } else if (hasLayout3) {
    // layout3 主题：数字人放大为默认的1.5倍，适配中等分辨率
    canvasSize = { width: 600, height: 600 };
  } else if (hasLayout4) {
    // layout4 主题：左右分栏，数字人适中尺寸
    canvasSize = { width: 500, height: 500 };
  } else {
    canvasSize = { width: 400, height: 400 };
  }

  // 组件样式类名
  const containerClass = [style.avatarHaloWrap, className].filter(Boolean).join(' ');

  const cardClass = [style.card, isFullScreen ? style.fullScreen : ''].filter(Boolean).join(' ');

  return (
    <div className={containerClass}>
      {/* 涟漪效果层 */}
      {shouldShowRipples && (
        <RippleEffect
          isActive={shouldShowRipples}
          dotCount={rippleConfig.dotCount}
          dotLifetime={rippleConfig.dotLifetime}
          dotInterval={rippleConfig.dotInterval}
          radiusRange={rippleConfig.radiusRange}
          sizeRange={rippleConfig.sizeRange}
          color={rippleConfig.color}
        />
      )}

      {/* 数字人卡片主体 */}
      <div className={cardClass}>
        {/* 视频渲染层 */}
        {targetVideoSrc && (
          <VideoRenderer
            videoSrc={targetVideoSrc}
            width={canvasSize.width}
            height={canvasSize.height}
            canStart={canStart}
            className={style.videoBg}
          />
        )}

        {/* 内容层 */}
        <div className={style.content}>{showUserTag && <UserTag name={scene} />}</div>
      </div>
    </div>
  );
}

export default AiAvatarCard;
